import styled from 'styled-components';
import { white, black } from 'styles/colors';
import { spacer8, spacer12, spacer1, spacer16, spacer80 } from 'styles/spacers';
import { scrollHorizontalInvisible } from 'styles/utils';
import { fontWeights } from 'styles/variables';
import { minWidth } from 'styles/media-queries';
import { levels } from 'styles/z-index';

export const Section = styled.section`
  display: grid;
  grid-column: full;
  grid-template-columns: subgrid;
  gap: ${spacer8};
`;

export const Title = styled.h2`
  grid-column: content;

  color: ${white};
  font-size: 1.4rem;
  font-weight: ${fontWeights.extra_bold};
`;

export const Caption = styled.p`
  grid-column: content;
  color: ${white};
  font-size: 1.2rem;
  line-height: 160%;
  font-weight: ${fontWeights.normal};
`;

export const RowWrapper = styled.div`
  position: relative;
  grid-column: full;
  display: grid;
  grid-template: subgrid;
`;

export const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: var(--audio_card-gap, ${spacer12});

  width: 100%;
  padding: 0 var(--carousel-gutter) ${spacer8};

  ${scrollHorizontalInvisible}
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
`;

export const Card = styled.div<{ selected: boolean }>`
  box-sizing: border-box;
  width: var(--audio_card-width, 144px);
  padding: ${spacer8};
  background: ${({ selected }) =>
    selected ? 'rgba(255, 255, 255, 0.14)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: ${spacer8};
  border: 2px solid;
  border-color: ${({ selected }) => (selected ? white : 'transparent')};
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  align-items: center;
  gap: ${spacer8};
  cursor: pointer;
  transition: border 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.14);

    button {
      background-color: #141414;
    }
  }

  &:active {
    background: rgba(255, 255, 255, 0.21);

    button {
      background-color: #343434;
    }
  }

  ${minWidth.lg`
    scroll-snap-align: start;
  `}
`;

export const CardImage = styled.img<{ type: 'voice' | 'music' }>`
  width: 128px;
  height: 128px;
  border-radius: ${({ type }) => (type === 'voice' ? '100%' : '1rem')};
`;

export const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacer1};
  width: 12.8rem;
`;

export const CardName = styled.div`
  color: ${white};
  font-size: 1.4rem;

  text-align: start;
  line-height: 110%;
  flex: 1;
  word-break: break-word;
  letter-spacing: 0%;
`;

export const PlayButton = styled.button<{ isPlaying: boolean }>`
  background-color: ${black};
  border: none;
  border-radius: 100%;
  flex: 0 0 auto;
  width: 3.2rem;
  height: 3.2rem;
  display: flex;
  align-items: center;
  justify-content: center;

  .voice-player-icon {
    width: ${({ isPlaying }) => (isPlaying ? '2rem' : '1.6rem')};
    height: 2rem;
  }
`;

export const Audio = styled.audio`
  display: none;
`;

export const ArrowButtonWrapper = styled.div<{
  $direction: 'left' | 'right';
  $isVisible: boolean;
  $isInitialized?: boolean;
}>`
  position: absolute;
  top: 0;
  bottom: 0;
  ${({ $direction }) => `${$direction}: -1px;`}
  width: ${spacer80};
  z-index: ${levels.highlight};

  --arrow-transition-duration: 0.3s;
  --arrow-transition-easing: cubic-bezier(0.4, 0, 0.2, 1);

  opacity: 0;
  pointer-events: none;
  transition: ${({ $isInitialized = true }) =>
    $isInitialized
      ? 'opacity var(--arrow-transition-duration) var(--arrow-transition-easing)'
      : 'none'};
  will-change: opacity;

  ${({ $isVisible }) =>
    $isVisible &&
    `
    opacity: 1;
    pointer-events: auto;
  `}

  @media (prefers-reduced-motion: reduce) {
    --arrow-transition-duration: 0.01ms;
  }

  display: none;

  ${minWidth.lg`
    display: flex;
    align-items: center;
    padding: 0 ${spacer16};
    justify-content: ${({
      // @ts-expect-error - minMax can not infer its parents type
      $direction,
    }) => ($direction === 'left' ? 'flex-start' : 'flex-end')};

    /* Gradient background based on direction */
    background: linear-gradient(
      ${({
        // @ts-expect-error - minMax can not infer its parents type
        $direction,
      }) => ($direction === 'left' ? '90deg' : '270deg')},
      ${black} 0%,
      transparent 100%
    );
  `}
`;
